import { styled } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { filter, transform } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import AttachFolderIcon from '../../assets/icons/AttachFolder.svg'
import CloseCircleIcon from '../../assets/icons/CloseCircle.svg?react'
import GridIcon from '../../assets/icons/GridIcon.svg'
import GripLinesIcon from '../../assets/icons/GripLines.svg?react'
import HurricaneIcon from '../../assets/icons/Hurricane.svg'
import LightBulbIcon from '../../assets/icons/LightBulb.svg'
import { CallingFunctionsForm } from '../../pages/InternalPage/RequestSettingPage/components/CallingFunctionsForm'
import { ContextSettingCard } from '../../pages/InternalPage/RequestSettingPage/components/ContextSettingCard'
import { Files } from '../../pages/InternalPage/RequestSettingPage/components/Files'
import { MemoryMessageCountStack } from '../../pages/InternalPage/RequestSettingPage/components/MemoryMessageCountStack'
import { RagSettingCard } from '../../pages/InternalPage/RequestSettingPage/components/RagSettingCard'
import { useFilesStore } from '../../store/storeFiles'
import { useRequestsStore } from '../../store/storeRequests'
import { useSettingStore } from '../../store/storeSetting'
import { useWrapperStore } from '../../store/storeWrapper'
import { LLMToolState } from '../../swagger/dto'

const getInitialValuesParams = (data, values) => {
    return transform(
        data,
        function (result, value, key) {
            const params = values?.length ? filter(values, { name: value.slug }) : null
            return (result[value.slug] =
                params && params[0] ? params[0].state : LLMToolState.ENABLED)
        },
        {},
    )
}

const TabsWrapper = styled(Grid)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))

interface Props {
    open: boolean
    handleClose: () => void
    type: string
}

export const ContextManagementDialog: React.FC<Props> = ({ open, handleClose, type }) => {
    const [customCallingFunctionsValues, setCustomRCallingFunctionsValues] = useState(undefined)
    const isRequestPage = type === 'request'
    const params = useParams()
    const [value, setValue] = useState('0')
    const [viewFileList, setViewFileList] = useState('card')
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
    }

    const handleChangeViewFileList = (event: React.SyntheticEvent, newValue: number) => {
        setViewFileList(newValue)
    }

    const { requestDetail, fetchRequestDetail, requestSettingLlmTools } = useRequestsStore(
        (state) => state,
    )

    const { fetchToolDetail, toolDetail } = useSettingStore((state) => state)
    const { fetchProjectDetail, projectDetail, modelDetail } = useWrapperStore((state) => state)
    const { fetchFilesUser } = useFilesStore((state) => state)

    useEffect(() => {
        if (isRequestPage && !requestDetail && params.requestId) {
            fetchRequestDetail({ threadId: +params.requestId })
        }
    }, [isRequestPage, requestDetail])

    useEffect(() => {
        if (!isRequestPage && !projectDetail) {
            fetchProjectDetail({ workplaceId: params.projectId })
        }
    }, [])

    useEffect(() => {
        if (value === '1') {
            fetchFilesUser({
                prefix: '/' + (isRequestPage ? `${params.requestId}` : `${params.projectId}`) + '/',
            })
        }
    }, [value])

    useEffect(() => {
        if (requestDetail && !modelDetail) {
            fetchToolDetail()
        }
    }, [requestDetail, modelDetail])

    useEffect(() => {
        if (!toolDetail) {
            fetchToolDetail()
        }
    }, [requestDetail, toolDetail])

    useEffect(() => {
        if (requestDetail && toolDetail) {
            setCustomRCallingFunctionsValues(
                getInitialValuesParams(toolDetail, requestDetail.llm_tools),
            )
        }
    }, [requestDetail, toolDetail])

    useEffect(() => {
        if (!params.requestId && toolDetail) {
            setCustomRCallingFunctionsValues(
                getInitialValuesParams(toolDetail, requestSettingLlmTools ?? []),
            )
        }
    }, [requestDetail, toolDetail, requestSettingLlmTools])

    return (
        <>
            <Dialog maxWidth="md" open={open} onClose={handleClose}>
                <DialogTitle sx={{ mb: 0 }}>
                    <Stack
                        direction="row"
                        sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography color="text" variant="h4" sx={{ fontWeight: 500 }}>
                            Управление контекстом
                        </Typography>
                        <IconButton
                            sx={{ height: '40px', padding: 0 }}
                            variant="text"
                            onClick={handleClose}>
                            <CloseCircleIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} m={0}>
                        <Grid size={12}>
                            <TabsWrapper>
                                <Tabs
                                    variant="scrollable"
                                    scrollButtons={false}
                                    value={value}
                                    onChange={handleChange}
                                    sx={{ borderBottom: 'none', padding: 0 }}>
                                    <Tab
                                        value="0"
                                        label="Контекст"
                                        icon={<HurricaneIcon />}
                                        iconPosition="start"
                                    />
                                    {params.requestId && (
                                        <Tab
                                            label="Вложения контекста"
                                            value="1"
                                            icon={<AttachFolderIcon />}
                                            iconPosition="start"
                                        />
                                    )}
                                    <Tab
                                        label="Вызов функций"
                                        value="2"
                                        icon={<LightBulbIcon />}
                                        iconPosition="start"
                                    />
                                </Tabs>
                                <Grid
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        display: 'flex',
                                    }}>
                                    {value === '1' && (
                                        <Tabs
                                            value={viewFileList}
                                            onChange={handleChangeViewFileList}
                                            sx={{
                                                borderBottom: 'none',
                                                padding: 0,
                                                alignItems: 'center',
                                            }}>
                                            <Tab
                                                variant="secondary"
                                                value="card"
                                                icon={<GridIcon />}
                                            />
                                            <Tab
                                                variant="secondary"
                                                value="table"
                                                icon={<GripLinesIcon />}
                                                id="1"
                                                sx={{ p: 0 }}
                                            />
                                        </Tabs>
                                    )}
                                </Grid>
                            </TabsWrapper>
                        </Grid>

                        {value === '0' && (
                            <Grid container>
                                <Grid container sx={{ maxWidth: '740px' }}>
                                    <MemoryMessageCountStack />
                                    <RagSettingCard />
                                </Grid>

                                <ContextSettingCard isRequestPage={isRequestPage} />
                            </Grid>
                        )}

                        {value === '1' && (
                            <Files
                                viewFileList={viewFileList}
                                isRequestPage={isRequestPage}
                                isReadOnly={false}
                            />
                        )}
                        {value === '2' && (
                            <CallingFunctionsForm
                                initialValues={customCallingFunctionsValues}
                                data={toolDetail}
                            />
                        )}
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    )
}
